import { formatDistanceStrict } from "date-fns";

/** What a connected integration's row says about it. */
export type RowStatus = "WORKING" | "NEEDS_ATTENTION" | "FAILING" | "NOT_CONNECTED" | "NO_RECENT_LEADS";

/** The parts of App.integrationHealth a row reads. */
export interface IntegrationHealthInfo {
    status: string;
    attempted: number;
    succeeded: number;
    failed: number;
    lastSuccessAt?: string | null;
    lastFailureAt?: string | null;
    lastFailureReason?: string | null;
    lastErrorMessage?: string | null;
}

export const STATUS_META: Record<RowStatus, { label: string; tone: "good" | "warn" | "bad" | "idle" }> = {
    WORKING: { label: "Working", tone: "good" },
    NEEDS_ATTENTION: { label: "Needs attention", tone: "warn" },
    FAILING: { label: "Leads aren't going through", tone: "bad" },
    NOT_CONNECTED: { label: "Not connected", tone: "bad" },
    NO_RECENT_LEADS: { label: "Waiting for a lead", tone: "idle" },
};

/** The filter chips, in order. */
export const STATUS_FILTERS: { key: "ALL" | "good" | "warn" | "bad" | "idle"; label: string }[] = [
    { key: "ALL", label: "All" },
    { key: "good", label: "Working" },
    { key: "warn", label: "Needs attention" },
    { key: "bad", label: "Not working" },
    { key: "idle", label: "Waiting for a lead" },
];

/**
 * A broken connection (isActive false: the integration stopped accepting our credentials) comes
 * first: deliveries to it can only fail. Otherwise the delivery health decides; an integration
 * with nothing sent in the window has no health entry.
 */
export function rowStatus(isActive: boolean | null | undefined, health: IntegrationHealthInfo | undefined): RowStatus {
    if (isActive === false) {
        return "NOT_CONNECTED";
    }
    switch (health?.status) {
        case "WORKING":
        case "NEEDS_ATTENTION":
        case "FAILING":
            return health.status;
        default:
            return "NO_RECENT_LEADS";
    }
}

const ago = (iso: string | null | undefined, now: Date) =>
    iso ? `${formatDistanceStrict(new Date(iso), now)} ago` : "";

/** A delivery failure in plain words, naming the integration. */
export function failureText(name: string, reason?: string | null, message?: string | null): string {
    const status = /^HTTP_(\d{3})$/.exec(reason || "")?.[1];
    if (status === "401" || status === "403") {
        return `${name} turned down our sign-in`;
    }
    if (status && status.startsWith("5")) {
        return `${name} had a server error`;
    }
    if (status) {
        return `${name} turned the lead down (${status})`;
    }
    if (reason === "STUCK_IN_PROGRESS") {
        return `${name} never answered`;
    }
    return message || `${name} returned an error`;
}

/** The line under the integration's name. */
export function statusLine(
    status: RowStatus,
    name: string,
    health: IntegrationHealthInfo | undefined,
    now: Date = new Date()
): string {
    switch (status) {
        case "WORKING":
            return [
                health?.lastSuccessAt ? `Last lead sent ${ago(health.lastSuccessAt, now)}` : "Sending leads",
                health?.attempted ? `${health.succeeded} of ${health.attempted} sent in the last 7 days` : "",
            ]
                .filter(Boolean)
                .join(" · ");
        case "NEEDS_ATTENTION":
            return `${health.failed} of the last ${health.attempted} leads didn't go through · last failed ${ago(
                health.lastFailureAt,
                now
            )}`;
        case "FAILING":
            return `The last lead didn't go through: ${failureText(
                name,
                health?.lastFailureReason,
                health?.lastErrorMessage
            )} · ${ago(health?.lastFailureAt, now)}`;
        case "NOT_CONNECTED":
            return `${name} isn't accepting our connection, so new leads aren't reaching it.`;
        default:
            return "No leads sent in the last 7 days";
    }
}
